/**
 * Cart Discount and Gift Card API Utilities
 * Handles Shopify cart discount codes and gift cards using Storefront GraphQL API
 */

import api from '../../js/api.js';

// Get the cart ID from the current cart
const getCartId = async (): Promise<string> => {
  try {
    const cartResponse = await fetch('/cart.js');
    const cart = await cartResponse.json();
    return cart.token; // Shopify cart token serves as cart ID for GraphQL
  } catch (error) {
    console.error('Error getting cart ID:', error);
    throw new Error('Failed to get cart ID');
  }
};

// GraphQL mutation for updating discount codes
const CART_DISCOUNT_CODES_UPDATE = `
  mutation cartDiscountCodesUpdate($cartId: ID!, $discountCodes: [String!]!) {
    cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $discountCodes) {
      cart {
        id
        discountCodes {
          code
          applicable
        }
        cost {
          totalAmount {
            amount
            currencyCode
          }
          subtotalAmount {
            amount
            currencyCode
          }
          totalTaxAmount {
            amount
            currencyCode
          }
          totalDutyAmount {
            amount
            currencyCode
          }
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;

// GraphQL mutation for updating gift card codes
const CART_GIFT_CARD_CODES_UPDATE = `
  mutation cartGiftCardCodesUpdate($cartId: ID!, $giftCardCodes: [String!]!) {
    cartGiftCardCodesUpdate(cartId: $cartId, giftCardCodes: $giftCardCodes) {
      cart {
        id
        appliedGiftCards {
          id
          lastCharacters
          amountUsed {
            amount
            currencyCode
          }
          balance {
            amount
            currencyCode
          }
        }
        cost {
          totalAmount {
            amount
            currencyCode
          }
          subtotalAmount {
            amount
            currencyCode
          }
          totalTaxAmount {
            amount
            currencyCode
          }
          totalDutyAmount {
            amount
            currencyCode
          }
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;

// GraphQL mutation for removing gift card codes
const CART_GIFT_CARD_CODES_REMOVE = `
  mutation cartGiftCardCodesRemove($cartId: ID!, $giftCardCodes: [String!]!) {
    cartGiftCardCodesRemove(cartId: $cartId, giftCardCodes: $giftCardCodes) {
      cart {
        id
        appliedGiftCards {
          id
          lastCharacters
          amountUsed {
            amount
            currencyCode
          }
          balance {
            amount
            currencyCode
          }
        }
        cost {
          totalAmount {
            amount
            currencyCode
          }
          subtotalAmount {
            amount
            currencyCode
          }
          totalTaxAmount {
            amount
            currencyCode
          }
          totalDutyAmount {
            amount
            currencyCode
          }
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;

// Execute GraphQL mutation
const executeGraphQLMutation = async (query: string, variables: any) => {
  try {
    const response = await fetch(api.getStorefrontGraphqlBaseUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': api.getStorefrontToken(),
      },
      body: JSON.stringify({
        query,
        variables,
      }),
    });

    if (!response.ok) {
      throw new Error(`GraphQL request failed: ${response.statusText}`);
    }

    const result = await response.json();
    
    if (result.errors) {
      throw new Error(`GraphQL errors: ${result.errors.map((e: any) => e.message).join(', ')}`);
    }

    return result.data;
  } catch (error) {
    console.error('GraphQL mutation error:', error);
    throw error;
  }
};

// Apply discount codes using Shopify Storefront GraphQL API
export const applyDiscountCodes = async (discountCodes: string[]) => {
  try {
    const cartId = await getCartId();
    const data = await executeGraphQLMutation(CART_DISCOUNT_CODES_UPDATE, {
      cartId: cartId.includes('gid://') ? cartId : `gid://shopify/Cart/${cartId}`,
      discountCodes,
    });

    const { cart, userErrors } = data.cartDiscountCodesUpdate;
    
    if (userErrors && userErrors.length > 0) {
      throw new Error(userErrors.map((error: any) => error.message).join(', '));
    }

    return cart;
  } catch (error) {
    console.error('Error applying discount codes:', error);
    throw error;
  }
};

// Remove discount codes (by setting empty array)
export const removeDiscountCodes = async (codesToRemove: string[]) => {
  try {
    const cartId = await getCartId();
    
    // Get current cart to find existing codes
    const cartResponse = await fetch('/cart.js');
    const currentCart = await cartResponse.json();
    const currentCodes = currentCart.discountCodes || [];
    
    // Filter out the codes to remove
    const remainingCodes = currentCodes.filter((code: string) => !codesToRemove.includes(code));
    
    const data = await executeGraphQLMutation(CART_DISCOUNT_CODES_UPDATE, {
      cartId: cartId.includes('gid://') ? cartId : `gid://shopify/Cart/${cartId}`,
      discountCodes: remainingCodes,
    });

    const { cart, userErrors } = data.cartDiscountCodesUpdate;
    
    if (userErrors && userErrors.length > 0) {
      throw new Error(userErrors.map((error: any) => error.message).join(', '));
    }

    return cart;
  } catch (error) {
    console.error('Error removing discount codes:', error);
    throw error;
  }
};

// Apply gift card codes using Shopify Storefront GraphQL API
export const applyGiftCardCodes = async (giftCardCodes: string[]) => {
  try {
    const cartId = await getCartId();
    const data = await executeGraphQLMutation(CART_GIFT_CARD_CODES_UPDATE, {
      cartId: cartId.includes('gid://') ? cartId : `gid://shopify/Cart/${cartId}`,
      giftCardCodes,
    });

    const { cart, userErrors } = data.cartGiftCardCodesUpdate;
    
    if (userErrors && userErrors.length > 0) {
      throw new Error(userErrors.map((error: any) => error.message).join(', '));
    }

    return cart;
  } catch (error) {
    console.error('Error applying gift card codes:', error);
    throw error;
  }
};

// Remove gift card codes using Shopify Storefront GraphQL API
export const removeGiftCardCodes = async (codesToRemove: string[]) => {
  try {
    const cartId = await getCartId();
    const data = await executeGraphQLMutation(CART_GIFT_CARD_CODES_REMOVE, {
      cartId: cartId.includes('gid://') ? cartId : `gid://shopify/Cart/${cartId}`,
      giftCardCodes: codesToRemove,
    });

    const { cart, userErrors } = data.cartGiftCardCodesRemove;
    
    if (userErrors && userErrors.length > 0) {
      throw new Error(userErrors.map((error: any) => error.message).join(', '));
    }

    return cart;
  } catch (error) {
    console.error('Error removing gift card codes:', error);
    throw error;
  }
};

// Validate discount code
export const validateDiscountCode = async (code: string) => {
  try {
    // This would typically be done server-side or through Shopify's validation
    // For now, we'll just check if the code is not empty and has a reasonable format
    if (!code || code.trim().length < 3) {
      throw new Error('Invalid discount code format');
    }
    
    return { valid: true, code: code.trim() };
  } catch (error) {
    console.error('Error validating discount code:', error);
    throw error;
  }
};

// Validate gift card code
export const validateGiftCardCode = async (code: string) => {
  try {
    // This would typically be done server-side or through Shopify's validation
    // For now, we'll just check if the code is not empty and has a reasonable format
    if (!code || code.trim().length < 8) {
      throw new Error('Invalid gift card code format');
    }
    
    return { valid: true, code: code.trim() };
  } catch (error) {
    console.error('Error validating gift card code:', error);
    throw error;
  }
};